import { useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'

const AUDIENCE_FILTERS_FIELDS = `
  ageGroup
  minAge
  maxAge
  skillLevels
  lessonTypeIds
  groupEventIds
  programIds
  facilityIds
  bookedWithinDays
  notBookedWithinDays
  studentIds
  excludedStudentIds
`

const CAMPAIGN_FIELDS = `
  id
  name
  body
  status
  editable
  cancelable
  recipientCount
  scheduledFor
  sentAt
  createdAt
  attributionEndsAt
  audienceFilters {
    ${AUDIENCE_FILTERS_FIELDS}
  }
  media {
    kind
    url
    title
    mimeType
  }
  offerings {
    id
    offeringType
    offeringId
    title
    bookingUrl
  }
`

export const COACH_CAMPAIGNS = gql`
  query CoachCampaigns {
    coachCampaigns {
      id
      name
      status
      recipientCount
      scheduledFor
      sentAt
      createdAt
      results {
        directBookingCount
        influencedBookingCount
        attributionWindowDays
      }
    }
  }
`

export const COACH_CAMPAIGN = gql`
  query CoachCampaign($id: ID!) {
    coachCampaign(id: $id) {
      ${CAMPAIGN_FIELDS}
    }
  }
`

export const COACH_CAMPAIGN_RESULTS = gql`
  query CoachCampaignResults($id: ID!) {
    coachCampaign(id: $id) {
      ${CAMPAIGN_FIELDS}
      results {
        recipientCount
        readCount
        repliedCount
        linkVisitCount
        directBookingCount
        influencedBookingCount
        reactivationEligibleCount
        reactivatedCount
        reactivationRate
        attributionWindowDays
        reactivationLookbackDays
      }
      recipients(limit: 200) {
        id
        contactName
        lastBookedAt
        visitedAt
        repliedAt
        conversionKind
        student {
          id
          firstName
          lastName
        }
      }
    }
  }
`

export const COACH_CAMPAIGN_AUDIENCE = gql`
  query CoachCampaignAudience(
    $filters: CoachCampaignAudienceFiltersInput
    $limit: Int
  ) {
    coachCampaignAudience(filters: $filters, limit: $limit) {
      count
      maxRecipients
      students {
        id
        firstName
        lastName
        isMinor
      }
    }
  }
`

export const COACH_CAMPAIGN_OPTIONS = gql`
  query CoachCampaignOptions(
    $past: DateTimeRangeInput
    $upcoming: DateTimeRangeInput
  ) {
    lessonTypes {
      id
      title
      status
    }
    currentUser {
      attributes {
        ... on Coach {
          facilities(withAvailability: false, withProfile: false) {
            id
            name
          }
          jrlPrograms {
            id
            name
            year
          }
        }
      }
      pastGroupEvents: groupEvents(
        limit: 100
        includeCanceled: false
        startDateTime: $past
        orderBy: [{ field: START_DATE_TIME, direction: DESC }]
      ) {
        id
        title
        startDateTime
      }
      upcomingGroupEvents: groupEvents(
        limit: 50
        includeCanceled: false
        startDateTime: $upcoming
        orderBy: [{ field: START_DATE_TIME, direction: ASC }]
      ) {
        id
        title
        startDateTime
      }
    }
  }
`

export const COACH_STUDENTS = gql`
  query CoachCampaignStudents {
    coachStudents {
      id
      firstName
      lastName
    }
  }
`

const RESPONSE_FIELDS = `
  success
  message
  campaign {
    ${CAMPAIGN_FIELDS}
  }
`

export const CREATE_COACH_CAMPAIGN = gql`
  mutation CreateCoachCampaign($input: CoachCampaignInput!) {
    createCoachCampaign(input: $input) {
      ${RESPONSE_FIELDS}
    }
  }
`

export const UPDATE_COACH_CAMPAIGN = gql`
  mutation UpdateCoachCampaign($id: ID!, $input: CoachCampaignInput!) {
    updateCoachCampaign(id: $id, input: $input) {
      ${RESPONSE_FIELDS}
    }
  }
`

export const SEND_COACH_CAMPAIGN = gql`
  mutation SendCoachCampaign($id: ID!, $scheduledFor: ISO8601DateTime) {
    sendCoachCampaign(id: $id, scheduledFor: $scheduledFor) {
      ${RESPONSE_FIELDS}
    }
  }
`

export const CANCEL_COACH_CAMPAIGN = gql`
  mutation CancelCoachCampaign($id: ID!) {
    cancelCoachCampaign(id: $id) {
      ${RESPONSE_FIELDS}
    }
  }
`

export const DELETE_COACH_CAMPAIGN = gql`
  mutation DeleteCoachCampaign($id: ID!) {
    deleteCoachCampaign(id: $id) {
      success
      message
    }
  }
`

export const DUPLICATE_COACH_CAMPAIGN = gql`
  mutation DuplicateCoachCampaign($id: ID!) {
    duplicateCoachCampaign(id: $id) {
      ${RESPONSE_FIELDS}
    }
  }
`

export const useCoachCampaigns = ({ skip } = {}) =>
  useQuery(COACH_CAMPAIGNS, { skip, errorPolicy: 'all' })

export const useCoachCampaign = (id, { skip } = {}) =>
  useQuery(COACH_CAMPAIGN, {
    variables: { id },
    skip: skip || !id,
    errorPolicy: 'all',
  })

export const useCoachCampaignResults = (id, { skip } = {}) =>
  useQuery(COACH_CAMPAIGN_RESULTS, {
    variables: { id },
    skip: skip || !id,
    errorPolicy: 'all',
  })

export const useCoachCampaignAudience = (filters, { skip, limit = 25 } = {}) =>
  useQuery(COACH_CAMPAIGN_AUDIENCE, {
    variables: { filters, limit },
    skip,
    errorPolicy: 'all',
  })

export const useCoachCampaignOptions = ({ skip } = {}) => {
  const [from] = useState(() => new Date().toISOString())
  return useQuery(COACH_CAMPAIGN_OPTIONS, {
    variables: { past: { to: from }, upcoming: { from } },
    skip,
  })
}

export const useCoachStudents = ({ skip } = {}) =>
  useQuery(COACH_STUDENTS, { skip })

export const useCreateCoachCampaign = () => useMutation(CREATE_COACH_CAMPAIGN)

export const useUpdateCoachCampaign = () => useMutation(UPDATE_COACH_CAMPAIGN)

export const useSendCoachCampaign = () => useMutation(SEND_COACH_CAMPAIGN)

export const useCancelCoachCampaign = () => useMutation(CANCEL_COACH_CAMPAIGN)

export const useDeleteCoachCampaign = () => useMutation(DELETE_COACH_CAMPAIGN)

export const useDuplicateCoachCampaign = () =>
  useMutation(DUPLICATE_COACH_CAMPAIGN)
